import { z } from 'zod';

/**
 * Tenant-scoped supplier directory. Same supplier can serve many projects,
 * so these live at the tenant root rather than under a project. CNPJ is
 * optional because small vendors (autonomous bricklayers) don't always
 * have one, but when present it must be a valid 14-digit numeric string
 * (format validation; no check-digit math here — we surface-validate only).
 */
export const cnpjSchema = z
  .string()
  .trim()
  .regex(/^\d{14}$/, 'CNPJ must be 14 digits without punctuation');

/**
 * CPF · 11 digits sem pontuação. Sem check-digit math · surface-validate.
 * Bid UX 2026-05-11: aceita fornecedor pessoa física (autônomo/MEI).
 */
export const cpfSchema = z
  .string()
  .trim()
  .regex(/^\d{11}$/, 'CPF deve ter 11 dígitos sem pontuação');

/**
 * Tipo de documento fiscal do fornecedor.
 *   'cnpj'  · pessoa jurídica
 *   'cpf'   · pessoa física / autônomo / MEI individual
 *   'none'  · fornecedor informal (PM aceita risco de compliance)
 */
export const supplierTaxIdTypeSchema = z.enum(['cnpj', 'cpf', 'none']);
export type SupplierTaxIdType = z.infer<typeof supplierTaxIdTypeSchema>;

/**
 * Wave 1.5 · 2026-05-10 · Canonical supplier categories.
 *
 * Slugs (snake_case) keep the UI legible and stable — labels live in
 * `apps/web/src/i18n` so PT-BR can be tweaked without re-migrating the
 * data column. Order is intentional · alphabetical inside the chip
 * cluster but the list below mirrors the typical PM sequence (matéria
 * bruta → estrutura → acabamento → serviços) so the picker scrolls in a
 * sensible cadence when render orders alphabetically anyway.
 */
export const SUPPLIER_CATEGORIES = [
  'materiais',
  'cimento_concreto',
  'areia_brita',
  'ferro_aco',
  'madeira',
  'hidraulica',
  'eletrica',
  'cobertura',
  'argamassa_imperm',
  'tintas',
  'vidro',
  'esquadrias',
  'aluminio',
  'ceramica',
  'metais_sanitarios',
  'isolamento',
  'equipamentos',
  'servicos',
] as const;
export type SupplierCategory = typeof SUPPLIER_CATEGORIES[number];
export const supplierCategorySchema = z.enum(SUPPLIER_CATEGORIES);

export const supplierSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  legalName: z.string(),
  displayName: z.string().nullable(),
  cnpj: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  pixKey: z.string().nullable(),
  notes: z.string().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Wave 100 — surfaced from the list endpoint so the row badge has it. */
  isSanctioned: z.boolean().optional(),
  cnpjStatus: z.string().nullable().optional(),
  cnpjEnrichedAt: z.string().nullable().optional(),
  /** Wave 100 expansion · raw JSON string from sanctions_detail column. */
  sanctionsDetail: z.string().nullable().optional(),
  /**
   * Wave 1.5 · 2026-05-10. JSON array of canonical category slugs the
   * supplier serves (e.g. `["hidraulica","materiais"]`). Used by the
   * list filter pills and supplier self-cadastro flow.
   */
  categories: z.array(z.string()).default([]),
  /**
   * Bid UX 2026-05-11. 'cnpj'|'cpf'|'none'. NULL pra rows legacy.
   */
  taxIdType: supplierTaxIdTypeSchema.nullable().optional(),
  /**
   * Wave geo · 2026-05-21 (migration 0251) — endereço opcional + geocoded
   * coordinates. Usados pelo painel "distância até a obra" e pelo mapa do
   * SupplierDetailsPage. Preenchidos via Google Places Autocomplete + backend
   * geocoding fallback. Todos opcionais (fornecedor informal / antigo pode
   * não ter endereço cadastrado).
   */
  address: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
  addressNormalized: z.string().nullable().optional(),
  cep: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  /**
   * Wave 83 · Simulation Mode.
   * When true, this is a synthetic supplier used for end-to-end flow testing.
   * Shown with a "SIMULAÇÃO" badge in the UI; never triggers real communications.
   */
  isSimulation: z.boolean().optional(),
});
export type Supplier = z.infer<typeof supplierSchema>;

const supplierBaseFields = {
  legalName: z.string().trim().min(2).max(200),
  displayName: z.string().trim().max(200).optional(),
  /**
   * Bid UX 2026-05-11. Documento opcional.
   * Se taxIdType='cnpj' → cnpj precisa ter 14 dígitos.
   * Se taxIdType='cpf'  → cnpj campo armazena CPF (11 dígitos).
   * Se taxIdType='none' → cnpj NULL.
   */
  cnpj: z.string().trim().max(20).optional(),
  taxIdType: supplierTaxIdTypeSchema.optional(),
  email: z.string().email().optional(),
  phone: z.string().trim().max(30).optional(),
  pixKey: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(2000).optional(),
  categories: z.array(supplierCategorySchema).max(12).optional(),
  /**
   * Wave geo · 2026-05-21. Endereço livre (preenchido pelo PM via Google
   * Places Autocomplete OU manualmente). Quando vem com lat/lng/placeId,
   * o backend pula o geocoding e persiste direto.
   */
  address: z.string().trim().max(300).optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  placeId: z.string().optional(),
} as const;

export const createSupplierRequestSchema = z
  .object(supplierBaseFields)
  .superRefine((v, ctx) => {
    // Coerência tipo × documento
    if (v.taxIdType === 'cnpj') {
      const digits = (v.cnpj ?? '').replace(/\D/g, '');
      if (digits.length !== 14) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CNPJ deve ter 14 dígitos.',
          path: ['cnpj'],
        });
      }
    } else if (v.taxIdType === 'cpf') {
      const digits = (v.cnpj ?? '').replace(/\D/g, '');
      if (digits.length !== 11) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'CPF deve ter 11 dígitos.',
          path: ['cnpj'],
        });
      }
    }
    // taxIdType='none' → cnpj pode/deve ser vazio · sem validação
  });
export type CreateSupplierRequest = z.infer<typeof createSupplierRequestSchema>;

export const updateSupplierRequestSchema = z
  .object(supplierBaseFields)
  .partial()
  .extend({
    archived: z.boolean().optional(),
  });
export type UpdateSupplierRequest = z.infer<typeof updateSupplierRequestSchema>;
